import { Conversation, CreateConversationDto, Message, SendMessageDto } from "@/types/chat";

const API_URL = process.env.REACT_APP_API_URL || "/api";

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

export const ChatService = {
  async getConversations(): Promise<Conversation[]> {
    const response = await fetch(`${API_URL}/chat/conversations`, {
      method: "GET",
      headers: getHeaders(),
    });
    if (!response.ok) throw new Error("Failed to fetch conversations");
    const data = await response.json();
    return data.data || data; // handle generic response wrapping if any
  },

  async createConversation(data: CreateConversationDto): Promise<Conversation> {
    const response = await fetch(`${API_URL}/chat/conversations`, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error("Failed to create conversation");
    const res = await response.json();
    return res.data || res;
  },

  async addParticipants(conversationId: string, participantIds: string[]): Promise<Conversation> {
    const response = await fetch(`${API_URL}/chat/conversations/${conversationId}/participants`, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({ participantIds }),
    });
    if (!response.ok) throw new Error("Failed to add participants");
    const res = await response.json();
    return res.data || res;
  },

  async removeParticipant(conversationId: string, userId: string): Promise<Conversation> {
    const response = await fetch(`${API_URL}/chat/conversations/${conversationId}/participants/${userId}`, {
      method: "DELETE",
      headers: getHeaders(),
    });
    if (!response.ok) throw new Error("Failed to remove participant");
    const res = await response.json();
    return res.data || res;
  },

  async deleteConversation(conversationId: string): Promise<void> {
    const response = await fetch(`${API_URL}/chat/conversations/${conversationId}`, {
      method: "DELETE",
      headers: getHeaders(),
    });
    if (!response.ok) throw new Error("Failed to delete conversation");
  },

  async getMessages(conversationId: string, limit = 20, cursor?: string): Promise<{ messages: Message[]; nextCursor?: string }> {
    const url = new URL(`${API_URL}/chat/conversations/${conversationId}/messages`, window.location.origin);
    url.searchParams.append("limit", limit.toString());
    if (cursor) url.searchParams.append("cursor", cursor);

    const response = await fetch(url.toString(), {
      method: "GET",
      headers: getHeaders(),
    });
    if (!response.ok) throw new Error("Failed to fetch messages");
    const res = await response.json();
    return {
      messages: res.data || res.messages || [],
      nextCursor: res.meta?.nextCursor,
    };
  },

  async sendMessage(conversationId: string, data: SendMessageDto | FormData, onProgress?: (progress: number) => void): Promise<Message> {
    const isFormData = data instanceof FormData;
    const headers: Record<string, string> = getHeaders();
    if (isFormData) {
      delete headers["Content-Type"];
    }

    if (isFormData && onProgress) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API_URL}/chat/conversations/${conversationId}/messages`);
        
        // Set headers
        Object.entries(headers).forEach(([key, value]) => {
          xhr.setRequestHeader(key, value);
        });

        // Upload progress
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            onProgress(percentComplete);
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const res = JSON.parse(xhr.responseText);
            resolve(res.data || res);
          } else {
            reject(new Error("Failed to send message"));
          }
        };

        xhr.onerror = () => reject(new Error("Network Error"));
        xhr.send(data);
      });
    }

    const response = await fetch(`${API_URL}/chat/conversations/${conversationId}/messages`, {
      method: "POST",
      headers,
      body: isFormData ? data : JSON.stringify(data),
    });
    if (!response.ok) throw new Error("Failed to send message");
    const res = await response.json();
    return res.data || res;
  },

  async reactToMessage(messageId: string, emoji: string, action: "add" | "remove"): Promise<void> {
    const response = await fetch(`${API_URL}/chat/messages/${messageId}/react`, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({ emoji, action }),
    });
    if (!response.ok) throw new Error("Failed to react to message");
  },

  async markAsRead(conversationId: string): Promise<void> {
    const response = await fetch(`${API_URL}/chat/conversations/${conversationId}/read`, {
      method: "PUT",
      headers: getHeaders(),
    });
    if (!response.ok) throw new Error("Failed to mark messages as read");
  },
};
